import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDomesticCartUpdateMutation } from '~/app/react-query/domestic/cart'
import type { DomesticCartConverterResponse } from '~/api/routes/domestic/cart/controller/type'
import { PostCartUpdateDto } from '~/api/routes/domestic/cart/controller/dto'
import type { DomesticCartLeisureOption } from '~/api/converters/domestic/cart/ProductSection/Leisure/index.type'
import type { DomesticCartPropertyOption } from '~/api/converters/domestic/cart/ProductSection/Property/index.type'
import { DOMESTIC_CART_QUERY_KEY } from '~/app/react-query/domestic/cart/constants'
import { getUseTimeText } from '~/api/converters/domestic/order/ProductSection/Property/utils'
import { DateTime } from 'luxon'
/**
 * 장바구니 옵션 변경을 위한 공통 hook
 * Optimistic update와 롤백 기능을 포함한 안전한 옵션 업데이트 기능을 제공합니다.
 */
export const useDomesticCartOptionUpdate = () => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateDomesticCart } = useDomesticCartUpdateMutation({
    onMutate: async (variables: PostCartUpdateDto['body']) => {
      // 1. 진행 중인 domesticCartQuery 리패치를 취소
      await queryClient.cancelQueries({ queryKey: [DOMESTIC_CART_QUERY_KEY] })

      // 2. 현재 데이터를 백업으로 저장
      const previousCartData = queryClient.getQueryData<DomesticCartConverterResponse>([
        DOMESTIC_CART_QUERY_KEY,
      ])

      // 3. Optimistic update 실행
      queryClient.setQueryData<DomesticCartConverterResponse>(
        [DOMESTIC_CART_QUERY_KEY],
        oldData => {
          if (!oldData) return oldData

          return {
            ...oldData,
            productSection: {
              ...oldData.productSection,
              cartItems: oldData.productSection.cartItems.map(item => {
                let filteredOptions: DomesticCartLeisureOption[] | DomesticCartPropertyOption[] = []
                switch (item.type) {
                  case 'DOMESTIC_LEISURE':
                    filteredOptions = item.options.map(option => {
                      if (option.optionId === variables.optionId) {
                        return {
                          ...option,
                          quantity: variables.domesticLeisure?.quantity,
                        } as DomesticCartLeisureOption
                      }
                      return option
                    })
                    break

                  case 'DOMESTIC_RESERVATION':
                    filteredOptions = item.options.map(option => {
                      if (option.optionId === variables.optionId) {
                        const checkInAt = variables.domesticReservation?.checkInAt ?? ''
                        const checkOutAt = variables.domesticReservation?.checkOutAt ?? ''
                        return {
                          ...option,
                          useTimeText: getUseTimeText(
                            DateTime.fromFormat(checkInAt, 'yyyy-MM-dd HH:mm').toISO(),
                            DateTime.fromFormat(checkOutAt, 'yyyy-MM-dd HH:mm').toISO(),
                            true
                          ),
                          checkInTime: checkInAt.split(' ')[1],
                          checkOutTime: checkOutAt.split(' ')[1],
                        } as DomesticCartPropertyOption
                      }
                      return option
                    })
                    break

                  default:
                    filteredOptions = item.options.map(option => option)
                }

                return {
                  ...item,
                  options: filteredOptions,
                } as typeof item
              }),
            },
          }
        }
      )

      return previousCartData
    },
    onError: (_error, _variables, previousData) => {
      if (previousData) {
        // 저장된 이전 데이터로 복구
        queryClient.setQueryData<DomesticCartConverterResponse>(
          [DOMESTIC_CART_QUERY_KEY],
          previousData
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [DOMESTIC_CART_QUERY_KEY] })
    },
  })

  const updateCartOption = useCallback(
    async (
      updateOption: PostCartUpdateDto['body'],
      callbacks?: {
        onSuccess?: () => void
        onError?: (error) => void
        onSettled?: () => void
      }
    ) => {
      await updateDomesticCart(updateOption, {
        onSuccess: () => {
          callbacks?.onSuccess?.()
        },
        onError: error => {
          callbacks?.onError?.(error)
        },
        onSettled: () => {
          callbacks?.onSettled?.()
        },
      })
    },
    [updateDomesticCart]
  )

  return {
    updateCartOption,
  }
}

export default useDomesticCartOptionUpdate
